import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IProfile } from '../interfaces/profile.interface';
import { map, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { IToken } from '../interfaces/token.interface';
import { TokenService } from '../token.service';
import { v4 as uuid } from 'uuid';
import { CryptoStorageService } from './storage-service.service';
import { IUserOrganizationsResponse } from '../interfaces/company.interface';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { getDeviceInfo } from '../helpers/device-info';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject = new BehaviorSubject<IProfile>(null);
  isLoggedIn$: Observable<boolean>;
  user$: Observable<IProfile> = this.userSubject.asObservable();
  headers = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
    accept: '*/*',
  });
  jsonHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    accept: '*/*',
  });
  headersDeviceId: any;

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private cryptoStorageService: CryptoStorageService,
    private msg: NzNotificationService,
  ) {
    this.isLoggedIn$ = this.tokenService.getToken().pipe(map(token => !!token));
    let uuidExist = localStorage.getItem('uuid');
    if (!uuidExist) {
      uuidExist = uuid();
      localStorage.setItem('uuid', uuidExist);
    }

    this.headersDeviceId = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      accept: '*/*',
      DEVICE_ID: uuidExist
    });
  }

  loginWithDeviceId(authData: any): Observable<any> {
    localStorage.removeItem('menu');
    localStorage.removeItem('MENU_ITEMS');
    if (!authData?.password) {
      this.msg.error('Ошибка', 'Перезайдите через логин и пароль!');
    }
    const { phoneNumber, password, client_customerId, client_idn } = authData;
    const body = `action_code=login&language=ru&grant_type=password&client_id=dbp-channels-business-web&auth_method=oauth&app_code=pre_session&username=${phoneNumber}&password=${encodeURIComponent(password)}&client_customerId=${client_customerId}&client_idn=${client_idn}`;
    return this.http.post('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headersDeviceId
    });
  }

  login(authData: any): Observable<IToken> {
    localStorage.removeItem('menu');
    localStorage.removeItem('MENU_ITEMS');
    const { phoneNumber, password, client_customerId, client_idn } = authData;
    const body = `action_code=login&language=ru&grant_type=password&client_id=dbp-channels-business-web&auth_method=oauth&username=${phoneNumber}&password=${encodeURIComponent(password)}&client_customerId=${client_customerId}&client_idn=${client_idn}`;
    return this.http.post<IToken>('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headersDeviceId
    });
  }

  loginWithAkkamal(): Observable<IToken> {
    localStorage.removeItem('menu');
    localStorage.removeItem('MENU_ITEMS');
    const body = `grant_type=password&client_id=dbp-channels-business-akkamal&app_code=pre_session&action_code=login&type=new`;
    const modifiedHeaders = this.headers.delete('Authorization');
    return this.http.post<IToken>('/auth/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: modifiedHeaders
    });
  }

  loginWith2fa(authData: any): Observable<IToken> {
    localStorage.removeItem('menu');
    localStorage.removeItem('MENU_ITEMS');
    const { phoneNumber, password, client_customerId, client_idn, sms_code } = authData;
    const body = `action_code=login&language=ru&grant_type=password&client_id=dbp-channels-business-web&auth_method=oauth&app_code=pre_session&username=${phoneNumber}&password=${encodeURIComponent(password)}&client_customerId=${client_customerId}&client_idn=${client_idn}&sms_code=${sms_code}`;
    return this.http.post<IToken>('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headersDeviceId
    });
  }

  loginQrByDevice(deviceCode: string, username: string): Observable<IToken> {
    localStorage.removeItem('menu');
    localStorage.removeItem('MENU_ITEMS');
    const body = `grant_type=password&client_id=dbp-channels-business-web-qr&username=${username}&device_code=${deviceCode}`;
    return this.http.post<IToken>('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headersDeviceId
    });
  }

  logout(): void {
    this.userSubject.next(null);
  }

  getQrCode(): Observable<HttpResponse<any>> {
    return this.http.post('/auth/realms/bank/devices', {}, {
      headers: this.headers,
      observe: 'response'
    });
  }

  getCompanysByQr(accessToken: string): Observable<HttpResponse<any>> {
    const headersQr = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      accept: '*/*',
      Authorization: `Bearer ${accessToken}`
    });
    return this.http.get(`/v1/dbp/users/profile`, {
      headers: headersQr,
      observe: 'response',
    });
  }

  setUser(user): void {
    this.userSubject.next(user);
  }

  getCompanys(phoneNumber: string, password: string): Observable<HttpResponse<IUserOrganizationsResponse>> {
    const body = {
      username: '+7' + phoneNumber,
      userPw: password,
      clientId: 'dbp-channels-business-web',
    };
    return this.http.post<IUserOrganizationsResponse>('/v1/dbp/users/user-organizations', body, {
      headers: this.jsonHeaders,
      observe: 'response'
    });
  }

  getQrAuthStatus(deviceCode: string): Observable<HttpResponse<any>> {
    return this.http.get(`/auth/realms/bank/devices?device_code=${deviceCode}`, {
      headers: this.headers,
      observe: 'response'
    });
  }

  refreshToken(refreshToken: string): Observable<IToken> {
    const AUTH_METHOD = localStorage.getItem('AUTH_METHOD');
    const clientId = AUTH_METHOD === 'web' ? 'dbp-channels-business-web' : AUTH_METHOD === 'qr' ? 'dbp-channels-business-web-qr' : 'dbp-channels-business-web';
    const body = `grant_type=refresh_token&client_id=${clientId}&refresh_token=${refreshToken}`;
    return this.http.post<IToken>('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headers
    });
  }

  refreshTokenWithCustomer(customerId: any, username: any, providerRefreshToken: string): Observable<IToken> {
    const clientId = 'dbp-channels-business-web';
    const body = `grant_type=password&username=${username}&client_id=${clientId}&action_code=refresh_token&provider_refresh_token=${providerRefreshToken}&customer_id=${customerId}`;
    return this.http.post<IToken>('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headers
    });
  }

  logOut(): Observable<HttpResponse<any>> {
    const clientId = 'dbp-channels-business-web';
    const REFRESH_TOKEN = this.cryptoStorageService.getFromSessionStorage('T')?.refresh_token;
    if (REFRESH_TOKEN) {
      const body = `grant_type=refresh_token&client_id=${clientId}&refresh_token=${REFRESH_TOKEN}`;
      return this.http.post(`/auth/realms/bank/protocol/openid-connect/logout`, body, {
        headers: this.headers,
        observe: 'response'
      });
    }
  }

  changeCompany(username: string, clientIdn: string): Observable<HttpResponse<IToken>> {
    const params = {
      language: 'ru',
      grant_type: 'password',
      client_id: 'dbp-channels-business-web',
      username,
      client_idn: clientIdn,
      switch_company: 'true'
    };
    const body = new URLSearchParams(params).toString();
    return this.http.post<IToken>('/auth/realms/bank/protocol/openid-connect/token', body, {
      headers: this.headersDeviceId,
      observe: 'response'
    });
  }

  sendAuthNotification(
    username: string,
    cliCode: string,
    authorizationMethod: 'password' | 'QR' | 'kaztoken'
  ): Observable<HttpResponse<any>> {
    const lang = localStorage.getItem('lang') || 'ru';

    const languageMap: Record<string, string> = {
      ru: 'ru-RU',
      en: 'en-US',
      kz: 'kk-KZ',
    };

    const acceptLanguage = languageMap[lang] || 'ru-RU';

    const body = {
      channel: 'WEB',
      authorizationMethod,
      username,
      clientCode: cliCode
    };

    const deviceInfo = getDeviceInfo();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: '*/*',
      'Accept-Language': acceptLanguage
    });

    if (deviceInfo.deviceModel) {
      headers = headers.set('X-Device-Model', deviceInfo.deviceModel);
    }
    if (deviceInfo.deviceOs) {
      headers = headers.set('X-Device-Os', deviceInfo.deviceOs);
    }
    if (deviceInfo.deviceOsVersion) {
      headers = headers.set('X-Device-Os-Version', deviceInfo.deviceOsVersion);
    }
    if (deviceInfo.browser) {
      headers = headers.set('X-Browser', deviceInfo.browser);
    }
    if (deviceInfo.appVersion) {
      headers = headers.set('X-App-Version', deviceInfo.appVersion);
    }

    return this.http.post<HttpResponse<any>>('/v1/dbp/business-notifications/auth-notifications', body, {
      headers,
      observe: 'response'
    });
  }

  getProfileWithRoles(): Observable<HttpResponse<any>> {
    const headers = new HttpHeaders({
      accept: '*/*',
      channel: 'WEB'
    });
    return this.http.get('/v1/dbp/users/profile-with-roles', {
      headers,
      observe: 'response'
    });
  }
}

